import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { api } from 'api/root';
import { maintenanceSlice } from 'store/maintenance/slice';
import { selectMaintenanceStatus } from 'store/maintenance/selectors';
import './Maintenance.scss';

const Maintenance: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMaintenanceMode = useAppSelector(selectMaintenanceStatus);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());
  const [isChecking, setIsChecking] = useState(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Update clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  // Calculate angles for clock hands
  const getClockAngles = (time: Date) => {
    const hours = time.getHours() % 12;
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();

    // Calculate angles (starting from 12 o'clock position)
    const hourAngle = (hours * 30) + (minutes * 0.5); // 30° per hour + minute adjustment
    const minuteAngle = minutes * 6; // 6° per minute
    const secondAngle = seconds * 6; // 6° per second
    return { hourAngle, minuteAngle, secondAngle };
  };

  const { hourAngle, minuteAngle, secondAngle } = getClockAngles(currentTime);

  const checkAPIStatus = async () => {
    if (isChecking) return;
    
    setIsChecking(true);
    try {
      // Try to reach user-info endpoint
      await api.get('/Account/user-info');
      // If we get here, the API is responding
      dispatch(maintenanceSlice.actions.setMaintenanceMode(false));
    } catch (error: any) {
      console.log('API still down, will retry in 30 seconds');
      setLastChecked(new Date());
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    if (!isMaintenanceMode) return;

    // Initial check
    checkAPIStatus();

    // Set up polling every 30 seconds
    const interval = setInterval(() => {
      checkAPIStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [isMaintenanceMode]);

  if (!isMaintenanceMode) return null;

  return (
    <div className="maintenance-page">
      <div className="maintenance-container">
        <div className="maintenance-icon">
          <svg
            width="120"
            height="120"
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer bezel ring */}
            <circle
              cx="100"
              cy="100"
              r="95"
              fill="url(#outerBezel)"
              stroke="url(#bezelStroke)"
              strokeWidth="3"
            />
            
            {/* Main watch face */}
            <circle
              cx="100"
              cy="100"
              r="85"
              fill="#ffffff"
              stroke="#e9ecef"
              strokeWidth="3"
            />
            
            {/* Hour markers (12, 3, 6, 9) */}
            <g className="hour-markers">
              {/* 12 o'clock */}
              <rect x="97" y="20" width="6" height="15" fill="#2c3e50" rx="2" />
              {/* 3 o'clock */}
              <rect x="165" y="97" width="15" height="6" fill="#2c3e50" rx="2" />
              {/* 6 o'clock */}
              <rect x="97" y="165" width="6" height="15" fill="#2c3e50" rx="2" />
              {/* 9 o'clock */}
              <rect x="20" y="97" width="15" height="6" fill="#2c3e50" rx="2" />
            </g>
            
            {/* Secondary hour markers */}
            {[1, 2, 4, 5, 7, 8, 10, 11].map(hour => {
              const angle = (hour * 30) - 90;
              const x = 100 + 70 * Math.cos(angle * Math.PI / 180);
              const y = 100 + 70 * Math.sin(angle * Math.PI / 180);
              
              return (
                <circle
                  key={hour}
                  cx={x}
                  cy={y}
                  r="3"
                  fill="#495057"
                />
              );
            })}
            
            {/* Minute markers */}
            {Array.from({ length: 60 }, (_, i) => {
              if (i % 5 !== 0) { // Skip hour positions
                const angle = (i * 6) - 90;
                const x1 = 100 + 75 * Math.cos(angle * Math.PI / 180);
                const y1 = 100 + 75 * Math.sin(angle * Math.PI / 180);
                const x2 = 100 + 70 * Math.cos(angle * Math.PI / 180);
                const y2 = 100 + 70 * Math.sin(angle * Math.PI / 180);
                
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="#adb5bd"
                    strokeWidth="1"
                  />
                );
              }
              return null;
            })}
            
            {/* Brand name "ROLEX" */}
            <text
              x="100"
              y="65"
              textAnchor="middle"
              fill="#2c3e50"
              fontSize="10"
              fontFamily="serif"
              fontWeight="bold"
              letterSpacing="3px"
            >
              ROLEX
            </text>
            
            {/* Model text */}
            <text
              x="100"
              y="145"
              textAnchor="middle"
              fill="#6c757d"
              fontSize="6"
              fontFamily="serif"
              letterSpacing="1px"
            >
              OYSTER PERPETUAL
            </text>
            
            {/* Hour hand (real-time position - thicker and more visible) */}
            <g transform={`rotate(${hourAngle} 100 100)`}>
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="55"
                stroke="#DB4014"
                strokeWidth="8"
                strokeLinecap="round"
                className="hour-hand"
                opacity="1"
              />
              {/* Hour hand highlight */}
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="55"
                stroke="#E55A2B"
                strokeWidth="6"
                strokeLinecap="round"
                opacity="0.8"
              />
            </g>
            
            {/* Minute hand (real-time position - thicker and more visible) */}
            <g transform={`rotate(${minuteAngle} 100 100)`}>
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="40"
                stroke="#DB4014"
                strokeWidth="6"
                strokeLinecap="round"
                className="minute-hand"
                opacity="1"
              />
              {/* Minute hand highlight */}
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="40"
                stroke="#E55A2B"
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.8"
              />
            </g>
            
            {/* Second hand (real-time position - thin and elegant) */}
            <g transform={`rotate(${secondAngle} 100 100)`}>
              {/* Second hand main line */}
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="110"
                stroke="#DB4014"
                strokeWidth="3"
                strokeLinecap="round"
              />
              {/* Second hand tip accent */}
              <circle
                cx="100"
                cy="30"
                r="2"
                fill="#E55A2B"
              />
            </g>
            
            {/* Center dot - refined and elegant */}
            <circle
              cx="100"
              cy="100"
              r="6"
              fill="url(#centerDotGradient)"
              stroke="#2c3e50"
              strokeWidth="2"
            />
            
            {/* Small center highlight */}
            <circle
              cx="100"
              cy="100"
              r="2"
              fill="#ffffff"
              opacity="0.8"
            />
            
            {/* Gradient definitions */}
            <defs>
              <radialGradient id="outerBezel" cx="50%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#e9ecef" />
                <stop offset="50%" stopColor="#dee2e6" />
                <stop offset="100%" stopColor="#adb5bd" />
              </radialGradient>
              
              <linearGradient id="bezelStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6c757d" />
                <stop offset="100%" stopColor="#495057" />
              </linearGradient>
              
              <radialGradient id="watchFace" cx="50%" cy="20%" r="90%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="60%" stopColor="#f8f9fa" />
                <stop offset="100%" stopColor="#e9ecef" />
              </radialGradient>
              
              <linearGradient id="faceStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#dee2e6" />
                <stop offset="100%" stopColor="#adb5bd" />
              </linearGradient>
              
              {/* Elegant hand gradients */}
              <linearGradient id="hourHandGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2c3e50" />
                <stop offset="50%" stopColor="#34495e" />
                <stop offset="100%" stopColor="#1a252f" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        
        <h1 className="maintenance-title">bMark is down for maintenance</h1>
        
        <p className="maintenance-description">
          We&rsquo;re currently performing scheduled maintenance to improve your experience.
          We&rsquo;ll be back online shortly.
        </p>
        
        <div className="maintenance-status">
          <div className="status-indicator">
            <div className={`status-dot ${isChecking ? 'checking' : 'waiting'}`}></div>
            <span className="status-text">
              {isChecking ? 'Checking status...' : 'Checking again in 30 seconds'}
            </span>
          </div>
         
        </div>
        
        <div className="maintenance-footer">
          <p>Thank you for your patience.</p>
        </div>
      </div>
    </div>
  );
};

export default Maintenance; 